import { motion } from 'framer-motion';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { IoTimer } from 'react-icons/io5';
import { toast } from 'react-toastify';
import { useSessionContext } from '../../contexts/SessionProvider';
import { TimerData } from '../../types/common';
import { calculateTimeLeft } from '../../utils/timeAndDate';

const SessionTimeReminder = () => {
  // integration of react hooks here
  const [isInitial, setIsInitial] = useState<boolean>(true);
  const [isHovering, setIsHovering] = useState<boolean>(false);
  const [isToastShown, setIsToastShown] = useState<boolean>(false);
  const [showReminder, setShowReminder] = useState<boolean>(false);

  const [timerData, setTimerData] = useState<TimerData>({
    days: -1,
    hours: -1,
    minutes: -1,
    seconds: -1,
  });

  // integration of context hooks here
  const { joinedClient, callHangUpHandler } = useSessionContext();

  // counting down time using timer here
  useEffect(() => {
    if (joinedClient?.end_time) {
      const timer = setInterval(
        () =>
          setTimerData(
            calculateTimeLeft(
              moment(joinedClient.end_time).format('YYYY-MM-DD'),
              moment(joinedClient.end_time).format('HH:mm:ss')
            )
          ),
        1000
      );

      return () => clearInterval(timer);
    }
  }, [joinedClient]);

  // removing associate & client from the session when time is up here
  useEffect(() => {
    if (
      timerData.days === 0 &&
      timerData.hours === 0 &&
      timerData.minutes === 0 &&
      timerData.seconds === 0 &&
      !isToastShown
    ) {
      toast.warn('Session Duration Ended!!', {
        toastId: `session-reminder-${timerData.minutes}`,
      });

      callHangUpHandler();

      setIsToastShown(true);
      setShowReminder(false);
    }
  }, [timerData, isToastShown, callHangUpHandler]);

  // started showing the time reminder when closing time is less than 5 mins here
  useEffect(() => {
    if (
      timerData.days === 0 &&
      timerData.hours === 0 &&
      timerData.minutes <= 2 &&
      timerData.minutes > 0 &&
      timerData.seconds === 0
    ) {
      setShowReminder(true);
    }
  }, [timerData]);

  // setting is initial showing false after 5 secs here
  useMemo(() => {
    const timeout = setTimeout(() => setIsInitial(false), 5000);

    return () => clearTimeout(timeout);
  }, []);

  // rendering session timer reminder component here
  return (
    <motion.div
      initial={{ right: -400 }}
      animate={showReminder ? { right: 20 } : { right: -400 }}
      onHoverStart={() => setIsHovering(true)}
      onHoverEnd={() => setIsHovering(false)}
      onClick={() => setIsHovering((state) => !state)}
      className='flex items-center justify-center rounded-lg absolute top-5 md:top-20 lg:top-1/2 !-translate-y-1/2 right-5 overflow-hidden z-[50] bg-base-white'
    >
      <motion.div
        initial={{ width: 0, padding: 0 }}
        animate={
          isHovering || isInitial
            ? { width: 200, padding: '8px 16px' }
            : { width: 0, padding: 0 }
        }
        className='text-base-orange duration-300 text-nowrap'
      >
        <p className='font-medium text-xs md:text-base'>
          {timerData.minutes} {timerData.minutes < 2 ? 'Minute' : 'Minutes'}{' '}
          {timerData.seconds} {timerData.seconds < 2 ? 'Second' : 'Seconds'}{' '}
          <br /> Left In This session!!
        </p>
      </motion.div>
      <div className='relative bg-base-orange text-base-white p-2 z-10'>
        <IoTimer className='size-8 md:size-12 xl:size-16' />
      </div>
    </motion.div>
  );
};

export default SessionTimeReminder;
